import { Check as CheckIcon } from "@tamagui/lucide-icons";
import React from "react";
import { Rect, Svg } from "react-native-svg";
import {
  GetProps,
  Checkbox as TamaguiCheckbox,
  XStack,
  YStack,
  getTokenValue,
  styled,
  useTheme,
  withStaticProperties,
} from "tamagui";

import { lineHeight } from "../../theme/fonts";
import { Label } from "../Typography";
import ErrorLabel from "./ErrorLabel";
import { useFormFieldAria } from "./useFormFieldAria";
import { useFormFieldIds } from "./useFormFieldIds";

function Check() {
  const theme = useTheme();
  return (
    <Svg width="24" height="24" viewBox="0 0 24 24">
      <Rect
        width="24"
        height="24"
        fill={theme.buttonHighlight?.val}
        ry="4"
        rx="4"
      />
      <CheckIcon color={theme.textButtonPrimary?.val} />
    </Svg>
  );
}

const CheckboxFrame = styled(TamaguiCheckbox, {
  width: "$300",
  height: "$300",
  borderRadius: "$50",
  borderWidth: "$strokeWidth",
  borderColor: "$textSecondary",
  shadowColor: "$iconBase",
  backgroundColor: "$transparent",
  marginVertical: "$25",
  outlineOffset: 2,
  pressStyle: {
    backgroundColor: "$transparent",
    shadowRadius: "$50",
  },
  hoverStyle: {
    borderColor: "$textSecondary",
    shadowRadius: "$25",
  },
  focusStyle: {
    borderColor: "$textSecondary",
    shadowRadius: "$25",
  },
  disabledStyle: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  variants: {
    error: {
      true: {
        borderColor: "$red",
      },
    },
  } as const,
});

const CheckboxLabel = styled(Label, {
  flexGrow: 1,
  flexShrink: 1,
  bold: undefined,
  size: "$sm",
  lineHeight: "$md",
  cursor: "pointer",
});

type CheckboxElement = React.ElementRef<typeof CheckboxFrame>;

type UnlabeledCheckboxProps = {
  id?: string;
  ref?: React.Ref<CheckboxElement>;
} & GetProps<typeof CheckboxFrame>;

const UnlabeledCheckbox = ({ id, ref, ...props }: UnlabeledCheckboxProps) => (
  <CheckboxFrame ref={ref} id={id} hitSlop={12} {...props}>
    <TamaguiCheckbox.Indicator>
      <Check />
    </TamaguiCheckbox.Indicator>
  </CheckboxFrame>
);

type LabeledCheckboxProps = {
  label?: string | React.ReactNode;
  labelProps?: GetProps<typeof Label>;
  rootProps?: GetProps<typeof XStack>;
  id?: string;
  ref?: React.Ref<CheckboxElement>;
  wrapped: boolean;
  errorText?: string;
} & GetProps<typeof TamaguiCheckbox>;

const LabeledCheckbox = ({
  label,
  labelProps = {},
  rootProps = {},
  wrapped,
  id,
  ref,
  onCheckedChange,
  errorText,
  disabled,
  ...props
}: LabeledCheckboxProps) => {
  const { fieldId: checkboxId, labelId, errorId } = useFormFieldIds(id);
  const formFieldAria = useFormFieldAria({
    hasLabel: typeof label === "string",
    labelId,
    errorId,
    errorText,
  });

  const labelFirstLineOffset = (lineHeight.md - getTokenValue("$300")) / 2;

  let stackStyle = {};
  let marginLeft;
  if (wrapped) {
    marginLeft = "$150";
    stackStyle = {
      ...stackStyle,
      width: "100%",
      backgroundColor: "$background",
      borderWidth: "$strokeWidth",
      borderRadius: "$100",
      borderColor: "$strokeSubdued",
      paddingRight: "$100",
      paddingVertical: "$150",
    };
  }

  return (
    <XStack alignItems="flex-start" {...stackStyle} {...rootProps}>
      <UnlabeledCheckbox
        ref={ref}
        id={checkboxId}
        marginRight="$100"
        marginLeft={marginLeft}
        marginVertical={0}
        transform={[{ translateY: labelFirstLineOffset }]}
        error={Boolean(errorText)}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        {...formFieldAria}
        {...props}
      />
      <YStack flex={1}>
        {typeof label === "string" ? (
          <CheckboxLabel
            id={labelId}
            htmlFor={checkboxId}
            {...labelProps}
            cursor={disabled ? "not-allowed" : "pointer"}
          >
            {label}
          </CheckboxLabel>
        ) : (
          label
        )}
        {errorText && (
          <ErrorLabel id={errorId} marginTop="$50">
            {errorText}
          </ErrorLabel>
        )}
      </YStack>
    </XStack>
  );
};

type CheckboxProps = {
  label?: string | React.ReactNode;
  labelProps?: GetProps<typeof Label>;
  ref?: React.Ref<CheckboxElement>;
  wrapped?: boolean;
  errorText?: string;
  rootProps?: GetProps<typeof XStack>;
} & GetProps<typeof TamaguiCheckbox>;

const Checkbox = ({
  label,
  ref,
  wrapped = false,
  errorText,
  rootProps,
  ...props
}: CheckboxProps) =>
  label ? (
    <LabeledCheckbox
      ref={ref}
      label={label}
      wrapped={wrapped}
      errorText={errorText}
      rootProps={rootProps}
      {...props}
    />
  ) : (
    <UnlabeledCheckbox ref={ref} {...props} />
  );

export default withStaticProperties(Checkbox, {
  Label: CheckboxLabel,
});
